import { z } from 'zod';

export const REQUIRED_ERROR = 'This field is required.';

export const SelectOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export const BubbleSchema = z.object({
  text: z
    .string({ required_error: REQUIRED_ERROR })
    .trim()
    .min(1, REQUIRED_ERROR)
    .max(100, 'It must be under 100 characters.'),
  selected: z.boolean({ required_error: REQUIRED_ERROR }).optional(),
});

export type Bubble = z.infer<typeof BubbleSchema>;

/**
 * Python reserved words that cannot be used as function names or variables
 * These are keywords in Python that would cause syntax errors
 */
export const pythonReservedWords = new Set([
  'False',
  'def',
  'if',
  'raise',
  'None',
  'del',
  'import',
  'return',
  'True',
  'elif',
  'in',
  'try',
  'and',
  'else',
  'is',
  'while',
  'as',
  'except',
  'lambda',
  'with',
  'assert',
  'finally',
  'nonlocal',
  'yield',
  'break',
  'for',
  'not',
  'class',
  'from',
  'or',
  'continue',
  'global',
  'pass',
  'async',
  'await',
]);

export const ReferenceIdSchema = z
  .string({ required_error: REQUIRED_ERROR })
  .min(1, { message: 'ID must be at least 1 characters' })
  .describe(
    'Unique identifier for the new entity, or set of entities. Must reference an existing entity or set of entities separated by a slash. ' +
      "Example: 'entity-1' or 'entity-1/entity-2'. ",
  );

export const IdSchema = z
  .string({ required_error: REQUIRED_ERROR })
  .trim()
  .min(1, { message: 'ID must be at least 1 characters after trimming' })
  .describe(
    'Unique identifier for the new entity. Must be generated client-side (e.g., UUID). ' +
      'Must not conflict with existing entity IDs.',
  );

export const StringSchema = z
  .string({ required_error: REQUIRED_ERROR })
  .trim()
  .describe('A text string with whitespace trimmed from both ends.');

export const NonEmptyStringSchema = z
  .string({ required_error: REQUIRED_ERROR })
  .trim()
  .min(1, REQUIRED_ERROR)
  .describe(
    'A required text string that cannot be empty. Minimum 1 character after trimming.',
  );

export const TrimmedStringSchema = StringSchema.trim();

export const SafeNameStringSchema = NonEmptyStringSchema.regex(
  // Safe name string: Latin letters (including extended), numbers, underscore, space and hyphen
  // Not using Unicode property escapes (\p{L}, \p{N}) because they are not supported by Babel
  // and would require ES2018+ runtime support
  /^[a-zA-Z0-9\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF_ -]+$/,
).describe("A safe string that doesn't contain any special characters.");

export const PositiveNumberSchema = z
  .number()
  .int()
  .nonnegative()
  .describe('A non-negative integer value (0 or greater).');

export const DateStringSchema = z
  .string()
  .datetime()
  .describe(
    "ISO 8601 formatted date-time string (e.g., '2024-01-15T10:30:00Z').",
  );

const optionalPythonicNamePattern = /^([a-zA-Z_][a-zA-Z0-9_]*)?$/;

export const OptionalPythonicNameSchema = z
  .string()
  .regex(optionalPythonicNamePattern, 'Invalid value, name should be Pythonic')
  .refine(
    name => !pythonReservedWords.has(name),
    'This is a reserved word in Python',
  )
  .optional()
  .default('');

const pythonicNamePattern = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export const PythonicNameSchema = z
  .string({ required_error: REQUIRED_ERROR })
  .min(1, REQUIRED_ERROR)
  .regex(pythonicNamePattern, 'Invalid value, name should be Pythonic')
  .refine(
    name => !pythonReservedWords.has(name),
    'This is a reserved word in Python',
  );

const isValidLanguageCode = (code: string) => {
  try {
    return Intl.getCanonicalLocales(code).length > 0;
  } catch {
    return false;
  }
};

export const LanguageCodeSchema = z
  .string({ required_error: 'Language code is required' })
  .refine(
    value => !value || isValidLanguageCode(value),
    'Invalid language code',
  )
  .describe(
    "BCP 47 language tag (e.g., 'en-US', 'fr-FR'). " +
      'Must be a valid language code recognized by Intl.getCanonicalLocales.',
  );

export const RegexStringSchema = NonEmptyStringSchema.describe(
  'Must be a valid regex pattern',
);

const integrationIdPattern = /^API-INTEGRATION-[a-fA-F0-9]{8}$/;

export const ApiIntegrationIdSchema = z
  .string()
  .regex(integrationIdPattern, 'Please provide a valid API integration ID');

export const CreatedAtSchema = DateStringSchema.describe(
  `${DateStringSchema.description} Timestamp when this entity was created`,
);
export const UpdatedAtSchema = DateStringSchema.describe(
  `${DateStringSchema.description} Timestamp when this entity was last updated. Can be undefined for newly created entities.`,
);
export const CreatedBySchema = StringSchema.describe(
  'User email who created this entity',
);
export const UpdatedBySchema = StringSchema.describe(
  'User email who last updated this entity. Can be undefined for newly created entities.',
);

export const EntityBaseSchema = z.object({
  id: IdSchema,
});

export const CreatedMetadataSchema = z.object({
  createdAt: CreatedAtSchema,
  createdBy: CreatedBySchema,
});
const UpdatedMetadataSchema = z.object({
  updatedAt: UpdatedAtSchema,
  updatedBy: UpdatedBySchema,
});
export const UpdatedMetadataSchemaOptional = UpdatedMetadataSchema.partial();
export const EntityMetadataSchema = CreatedMetadataSchema.merge(
  UpdatedMetadataSchema,
);
export const EntityMetadataOptionalSchema = EntityMetadataSchema.partial();

export type EntityMetadata = z.infer<typeof EntityMetadataSchema>;

export function createEntityAdapterSchema<T extends z.ZodTypeAny>(
  entitySchema: T,
) {
  return z
    .object({
      ids: z.array(IdSchema).describe('Ordered array of entity IDs'),
      entities: z
        .record(IdSchema, entitySchema)
        .describe('Map of entity ID to entity object'),
    })
    .describe('Normalized entity collection using the entity adapter pattern');
}

export function findDuplicateIds<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const duplicates: T[] = [];

  for (const item of items) {
    if (seen.has(item.id)) {
      duplicates.push(item);
    } else {
      seen.add(item.id);
    }
  }

  return duplicates;
}

export function findDuplicateNames<T extends { name: string }>(
  items: T[],
): T[] {
  const nameCount = new Map<string, number>();

  for (const item of items) {
    nameCount.set(item.name, (nameCount.get(item.name) ?? 0) + 1);
  }

  return items.filter(item => (nameCount.get(item.name) ?? 0) > 1);
}
